import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { Cifra, Cifras } from '@/components/ui/cifras';
import { Etiqueta } from '@/components/ui/etiqueta';
import { EstadoError, EstadoVacio } from '@/components/ui/estado-vacio';
import { GrupoDeOpciones } from '@/components/ui/grupo-de-opciones';
import { CambioDePrecio } from '@/components/ui/precio';
import { Skeleton } from '@/components/ui/skeleton';
import { History, RotateCcw, ShieldCheck, User as UserIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  cantidadDe,
  formatoFecha,
  formatoFechaBolivia,
  formatoNumero,
  formatoPorcentaje,
  formatoPrecio,
} from '@/lib/utils/formato-precio';
import type {
  OutcomeData,
  ProductHistoryEntry,
  ProductHistoryResponse,
} from '../types/smart-pricing.types';
import { STRATEGY_LABELS } from '../types/smart-pricing.types';
import { smartPricingApi } from '../services/smart-pricing-api';
import { EsqueletoFilas, AvisoCargando } from './skeletons';
import { ReglasHoyDelProducto } from '@/features/pricing-rules/components/reglas-hoy-del-producto';
import { HistorialDePrecios } from './historial-de-precios';
import { VolverAEstePrecioDialog } from './volver-a-este-precio-dialog';
import type { CambioDelHistorial } from '../types/historial-de-precios.types';
import { quedoEnTexto } from '../lib/volver-a-este-precio';
import { useAuth } from '@/features/auth/hooks/use-auth';
import { useToast } from '@/lib/hooks/use-toast';
import { CUALQUIER_FECHA, enElRango, type RangoDeFechas } from '../lib/rango-de-fechas';
import { EncabezadoDeVentana } from '@/features/compite/ui/ventana-compite';

/**
 * El historial del producto (pulido, spec 232; lote C2b, spec 246 punto 11 y
 * maqueta `c2-historial`). Arriba, plegada, la explicación de hoy; después el
 * historial único de los cambios de precio, venga de donde venga el cambio; y
 * al final, plegado, el detalle del producto viejo (`DynamicPriceLog`): la
 * misma información de antes, con los colores del tema, sin violeta, chispas,
 * franjas ni rótulos en mayúsculas.
 */

interface ProductHistoryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  productId: string | null;
  productName: string;
  /** Lote C2b: después de volver a un precio (la ficha actualiza su precio de hoy). */
  onVolvio?: (precio: number) => void;
}

const VENTANAS = [
  { valor: '7', texto: '7 días' },
  { valor: '14', texto: '14 días' },
  { valor: '30', texto: '30 días' },
] as const;
type Ventana = (typeof VENTANAS)[number]['valor'];

/** «4 mar 2025, 08:00»: el mismo formato que el registro nuevo (historial-de-precios),
 * siempre con el año, en hora de Bolivia — las dos partes del diálogo se leen igual. */
function cuando(iso: string): string {
  return formatoFechaBolivia(iso, { hora: true });
}

/** «25 %», «12,55 %»: el parámetro tal cual se guardó, sin redondear. */
const pct = (n: number) => formatoNumero(n) + ' %';

/**
 * Los parámetros de la estrategia de antes, en castellano. Los escribió el motor
 * de antes de la pieza 0 (vault 231): ahí el margen objetivo y el mínimo del
 * Precio inteligente eran un recargo sobre el costo, así que el margen va sin su
 * base; sólo «Apuntar a un margen» ya lo medía sobre el precio (auditoría I5).
 */
export function parametrosEnPalabras(
  config: Record<string, unknown> | null | undefined,
  strategy?: string | null
): string {
  if (!config) return '—';
  const partes: string[] = [];
  if (typeof config.targetMargin === 'number')
    partes.push(
      strategy === 'MARGIN_TARGET'
        ? `margen ${pct(config.targetMargin)} sobre el precio`
        : `margen objetivo ${pct(config.targetMargin)}`
    );
  if (typeof config.undercutPercent === 'number')
    partes.push(`${pct(config.undercutPercent)} debajo del más barato`);
  if (typeof config.minMarginPercent === 'number')
    partes.push(`margen mínimo ${pct(config.minMarginPercent)}`);
  return partes.length > 0 ? partes.join(' · ') : '—';
}

/** Un bloque de la explicación de antes: un título chico y su contenido, sin franja. */
function Bloque({ titulo, children }: { titulo: string; children: ReactNode }) {
  return (
    <div className="space-y-1 rounded-md border bg-muted/30 p-2.5 text-xs">
      <p className="font-semibold text-foreground/80">{titulo}</p>
      {children}
    </div>
  );
}

function Lineas({ lineas }: { lineas: string[] }) {
  return (
    <ul className="space-y-0.5 text-foreground/80">
      {lineas.map((l, i) => (
        <li key={`${i}-${l}`}>{l}</li>
      ))}
    </ul>
  );
}

function ResultadosDespues({ entry }: { entry: ProductHistoryEntry }) {
  const [ventana, setVentana] = useState<Ventana>('7');
  const [outcome, setOutcome] = useState<OutcomeData | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(
    async (w: 7 | 14 | 30) => {
      setLoading(true);
      try {
        setOutcome(await smartPricingApi.getOutcome(entry.id, w));
      } catch {
        setOutcome(null);
      } finally {
        setLoading(false);
      }
    },
    [entry.id]
  );

  useEffect(() => {
    void load(Number(ventana) as 7 | 14 | 30);
  }, [load, ventana]);

  return (
    <div className="space-y-2 rounded-md border bg-muted/30 p-2.5 text-xs">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="font-semibold text-foreground/80">Resultados después del cambio</p>
        <GrupoDeOpciones
          aria-label="Período"
          opciones={[...VENTANAS]}
          valor={ventana}
          onCambiar={setVentana}
        />
      </div>

      {loading && <Skeleton className="h-12 w-full" />}

      {!loading && outcome && !outcome.hasData && (
        <p className="text-muted-foreground">
          Sin ventas registradas en este período: los resultados aparecen cuando se carguen las
          ventas.
        </p>
      )}

      {!loading && outcome && outcome.hasData && (
        <>
          <Cifras>
            <Cifra chica valor={outcome.totalUnits} rotulo="unidades vendidas" />
            <Cifra
              chica
              valor={outcome.avgSellingPrice != null ? formatoPrecio(outcome.avgSellingPrice) : '—'}
              rotulo="precio promedio real (Bs)"
            />
          </Cifras>
          {/* Lote A del 2-oct (A4): sin la velocidad de ventas («ventas por mes frente a
              antes», «Antes vendía…»): la velocidad compara contra una base que no es un
              dato real. Las unidades, los ingresos y los registros sí lo son. */}
          <p className="text-muted-foreground">
            {cantidadDe(outcome.dataPoints, 'registro de venta', 'registros de venta')} · ingresos
            Bs {formatoPrecio(outcome.totalRevenue)}
          </p>
        </>
      )}
    </div>
  );
}

/** Cómo calculó la estrategia de antes ese precio: factores, mercado, confianza. */
function ComoLoCalculo({ entry }: { entry: ProductHistoryEntry }) {
  const snap = entry.snapshot;
  const cfg = entry.config;
  const strategy = entry.strategy;

  // ── Sin retrato: lo que dice la configuración ──
  if (!snap) {
    const lineas: string[] = [];
    const minimo =
      cfg?.minMarginPercent != null ? `Margen mínimo cuidado: ${pct(cfg.minMarginPercent)}` : null;
    if (strategy === 'SMART_PRICING') {
      lineas.push('Mira el mercado, la demanda, el inventario y la elasticidad');
      if (minimo) lineas.push(minimo);
      if (cfg?.targetMargin != null) lineas.push(`Margen objetivo: ${pct(cfg.targetMargin)}`);
    } else if (strategy === 'MATCH_LOWEST') {
      lineas.push(STRATEGY_LABELS.MATCH_LOWEST);
      if (minimo) lineas.push(minimo);
    } else if (strategy === 'UNDERCUT_PERCENT') {
      lineas.push(
        cfg?.undercutPercent != null
          ? `Precio ${pct(cfg.undercutPercent)} debajo del más barato`
          : 'Precio debajo del más barato'
      );
      if (minimo) lineas.push(minimo);
    } else if (strategy === 'MARGIN_TARGET') {
      lineas.push(
        cfg?.targetMargin != null
          ? `Precio para ganar ${pct(cfg.targetMargin)} sobre el precio`
          : 'Precio para ganar el margen objetivo sobre el precio'
      );
    }
    if (lineas.length === 0) return null;
    return (
      <Bloque titulo="Estrategia aplicada">
        <Lineas lineas={lineas} />
      </Bloque>
    );
  }

  // ── Con retrato: los factores y el mercado de ese momento ──
  const estrategia = snap.strategy as string | undefined;
  const tiendas: Array<{ name: string; price: number; scrapedAt?: string }> =
    snap.competitorStores ?? [];
  const confianza: number | undefined = snap.confidenceScore;
  const protegido = snap.marginProtectionApplied === true;

  let factores: ReactNode = null;
  if (estrategia === 'SMART_PRICING') {
    const todos: Array<{ signal: string; dataAvailable: boolean; adjustment?: number }> =
      snap.factors ?? [];
    const activos = todos.filter((f) => f.dataAvailable && Math.abs(f.adjustment ?? 0) > 0.001);
    const sinDatos = todos.filter((f) => !f.dataAvailable);
    const lineas: string[] = [];
    // `basePriceLabel` no se muestra (auditoría M6): lo escribió el motor viejo con
    // su formato («costo × 1.25», con punto) y su base de entonces; reescribirlo
    // podría decir algo que no pasó, y mostrarlo tal cual rompe el formato único.
    if (typeof snap.totalAdjustment === 'number')
      lineas.push(`Ajuste total: ${formatoPorcentaje(snap.totalAdjustment, { signo: true })}`);
    factores = (
      <Bloque titulo="Cómo lo calculó">
        <Lineas lineas={lineas} />
        {activos.length > 0 && (
          <ul className="space-y-0.5">
            {activos.map((f) => (
              <li key={f.signal} className="flex items-center justify-between gap-2">
                <span className="text-foreground/80">{f.signal}</span>
                <span
                  className={cn(
                    'font-semibold tabular-nums',
                    (f.adjustment ?? 0) > 0 ? 'text-ok' : 'text-baja'
                  )}
                >
                  {formatoPorcentaje((f.adjustment ?? 0) * 100, { signo: true })}
                </span>
              </li>
            ))}
          </ul>
        )}
        {sinDatos.length > 0 && (
          <p className="text-muted-foreground">
            Sin datos: {sinDatos.map((f) => f.signal).join(' · ')}
          </p>
        )}
        {protegido && (
          <Etiqueta tono="alerta">
            <ShieldCheck aria-hidden="true" className="h-3 w-3" />
            Lo protegió el margen mínimo
          </Etiqueta>
        )}
      </Bloque>
    );
  } else if (estrategia === 'MATCH_LOWEST' || estrategia === 'UNDERCUT_PERCENT') {
    const lineas: string[] = [];
    if (estrategia === 'UNDERCUT_PERCENT' && snap.undercutPercent != null)
      lineas.push(`${pct(snap.undercutPercent)} debajo del más barato`);
    if (estrategia === 'MATCH_LOWEST' && snap.note) lineas.push(snap.note);
    if (snap.competitorMin != null)
      lineas.push(`El más barato del mercado: Bs ${formatoPrecio(snap.competitorMin)}`);
    if (estrategia === 'UNDERCUT_PERCENT' && snap.note) lineas.push(snap.note);
    factores =
      lineas.length > 0 ? (
        <Bloque titulo="Cómo estaba el mercado">
          <Lineas lineas={lineas} />
        </Bloque>
      ) : null;
  } else if (estrategia === 'MARGIN_TARGET') {
    const lineas: string[] = [];
    if (snap.targetMargin != null)
      lineas.push(`Margen objetivo: ${pct(snap.targetMargin)} sobre el precio`);
    if (snap.costPrice != null) lineas.push(`Costo: Bs ${formatoPrecio(snap.costPrice)}`);
    if (snap.competitorAvg != null)
      lineas.push(`Promedio del mercado: Bs ${formatoPrecio(snap.competitorAvg)}`);
    factores =
      lineas.length > 0 ? (
        <Bloque titulo="Los datos que usó">
          <Lineas lineas={lineas} />
        </Bloque>
      ) : null;
  }

  const mercado =
    tiendas.length > 0 ? (
      <Bloque titulo="La competencia en ese momento">
        <ul className="space-y-0.5">
          {tiendas.map((s, i) => (
            <li key={`${i}-${s.name}`} className="flex items-center justify-between gap-2">
              <span className="truncate text-foreground/80">{s.name}</span>
              <span className="flex items-center gap-2">
                <span className="font-semibold tabular-nums">Bs {formatoPrecio(s.price)}</span>
                {s.scrapedAt && (
                  <span className="text-muted-foreground">{formatoFecha(s.scrapedAt)}</span>
                )}
              </span>
            </li>
          ))}
        </ul>
        {snap.competitorAvg != null && (
          <p className="border-t pt-1 text-muted-foreground">
            Promedio: Bs {formatoPrecio(snap.competitorAvg)} · el más barato:{' '}
            {snap.competitorMin != null ? `Bs ${formatoPrecio(snap.competitorMin)}` : '—'}
          </p>
        )}
      </Bloque>
    ) : snap.competitorCount != null && snap.competitorCount > 0 ? (
      <Bloque titulo="El mercado en ese momento">
        <p className="text-foreground/80">
          {cantidadDe(snap.competitorCount, 'tienda', 'tiendas')} · el más barato:{' '}
          {snap.competitorMin != null ? `Bs ${formatoPrecio(snap.competitorMin)}` : '—'} · promedio:{' '}
          {snap.competitorAvg != null ? `Bs ${formatoPrecio(snap.competitorAvg)}` : '—'}
        </p>
      </Bloque>
    ) : null;

  const confianzaEnBarra =
    confianza != null ? (
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <span>Confianza del cálculo: {formatoPorcentaje(confianza, { decimales: 0 })}</span>
        <div className="h-1.5 w-20 overflow-hidden rounded-full bg-muted">
          <div
            className={cn(
              'h-full rounded-full',
              confianza >= 70 ? 'bg-ok' : confianza >= 40 ? 'bg-alerta' : 'bg-bloqueo'
            )}
            style={{ width: `${confianza}%` }}
          />
        </div>
      </div>
    ) : null;

  if (!factores && !mercado && !confianzaEnBarra) return null;
  return (
    <div className="space-y-2">
      {factores}
      {mercado}
      {confianzaEnBarra}
    </div>
  );
}

function PrecioDeAntesADespues({ antes, despues }: { antes?: number; despues?: number }) {
  const cambio =
    antes != null && despues != null && Math.abs(despues - antes) > 0.005 ? despues - antes : null;
  return (
    <div className="flex flex-wrap items-center gap-2 rounded bg-muted/30 p-2 text-xs">
      <span className="text-muted-foreground">Precio:</span>
      <span className="tabular-nums">
        {antes != null ? `Bs ${formatoPrecio(antes)}` : '—'}
        {despues != null && ` → Bs ${formatoPrecio(despues)}`}
      </span>
      {cambio != null ? (
        <CambioDePrecio
          diferencia={cambio}
          porcentaje={antes! > 0 ? (cambio / antes!) * 100 : null}
        />
      ) : (
        <span className="text-muted-foreground">sin cambio de precio</span>
      )}
    </div>
  );
}

function EntradaDeAntes({
  entry,
  esLaUltima,
}: {
  entry: ProductHistoryEntry;
  esLaUltima: boolean;
}) {
  const previo = entry.previousValue?.currentPrice as number | undefined;

  // Una vuelta al precio base: la tarjeta corta.
  if (entry.action === 'RESET_TO_BASE_PRICE') {
    const vuelta = entry.newValue?.currentPrice as number | undefined;
    return (
      <li className="space-y-1.5 px-3 py-2.5 text-sm">
        <div className="flex items-center justify-between gap-2">
          <span className="flex items-center gap-1.5 font-medium">
            <RotateCcw aria-hidden="true" className="h-3.5 w-3.5 text-muted-foreground" />
            Volvió al precio de lista
            {entry.details?.source === 'bulk_reset' && (
              <span className="text-xs font-normal text-muted-foreground">
                (con otros a la vez)
              </span>
            )}
          </span>
          <span className="whitespace-nowrap text-xs text-muted-foreground">
            {cuando(entry.createdAt)}
          </span>
        </div>
        {previo != null && vuelta != null && (
          <PrecioDeAntesADespues antes={previo} despues={vuelta} />
        )}
        {entry.userName && entry.userName !== 'Desconocido' && (
          <p className="flex items-center gap-1 text-xs text-muted-foreground">
            <UserIcon aria-hidden="true" className="h-3 w-3" />
            {entry.userName}
          </p>
        )}
      </li>
    );
  }

  const despues: number | undefined = entry.priceUpdated
    ? (entry.simulatedPrice ?? (entry.newValue?.currentPrice as number | undefined))
    : ((entry.newValue?.currentPrice as number | undefined) ?? previo);
  const anterior = (entry.previousValue?.strategy as string | undefined) ?? null;
  const nombre = (s: string | null | undefined) =>
    s ? (STRATEGY_LABELS[s as keyof typeof STRATEGY_LABELS] ?? s) : '—';

  return (
    <li className="space-y-2 px-3 py-2.5 text-sm">
      <div className="flex items-start justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-semibold">{nombre(entry.strategy)}</span>
          {esLaUltima && <Etiqueta>el último</Etiqueta>}
          {/* Neutra: cambiar el precio no es bueno ni malo (auditoría M6). */}
          {entry.priceUpdated && <Etiqueta>cambió el precio</Etiqueta>}
          {entry.basePriceUsed && (
            <Etiqueta tono="info">
              desde el precio de lista
              {entry.basePriceValue != null && ` ${formatoPrecio(entry.basePriceValue)}`}
            </Etiqueta>
          )}
        </div>
        <span className="whitespace-nowrap text-xs text-muted-foreground">
          {cuando(entry.createdAt)}
        </span>
      </div>

      <div className="grid grid-cols-2 gap-2 text-xs">
        <div>
          <p className="text-muted-foreground">Estrategia</p>
          {anterior && anterior !== entry.strategy ? (
            <p>
              {nombre(anterior)} → <span className="font-semibold">{nombre(entry.strategy)}</span>
            </p>
          ) : (
            <p className="font-semibold">{nombre(entry.strategy)}</p>
          )}
        </div>
        <div>
          <p className="text-muted-foreground">Parámetros</p>
          <p>{parametrosEnPalabras(entry.config, entry.strategy)}</p>
        </div>
      </div>

      {(previo != null || despues != null) && (
        <PrecioDeAntesADespues antes={previo} despues={despues} />
      )}

      <ComoLoCalculo entry={entry} />

      {entry.rulesTrace && entry.rulesTrace.length > 0 && (
        <Bloque titulo="Las reglas que lo limitaron">
          <ul className="space-y-0.5">
            {entry.rulesTrace.map((t, i) => (
              <li key={`${i}-${t.ruleId}`}>
                «{t.ruleName}»:{' '}
                <span className="tabular-nums">
                  Bs {formatoPrecio(Number(t.priceBefore))} →{' '}
                  <span className="font-semibold">Bs {formatoPrecio(Number(t.priceAfter))}</span>
                </span>
                {t.warnings && t.warnings.length > 0 && (
                  <span className="ml-2 text-alerta">{t.warnings.join('; ')}</span>
                )}
              </li>
            ))}
          </ul>
        </Bloque>
      )}

      {entry.priceUpdated && <ResultadosDespues entry={entry} />}

      {/* «Desconocido» no es un nombre (auditoría M6): sin quién, no se dice. */}
      {entry.userName && entry.userName !== 'Desconocido' && (
        <p className="flex items-center gap-1.5 border-t pt-1.5 text-xs text-muted-foreground">
          <UserIcon aria-hidden="true" className="h-3 w-3" />
          Lo aplicó <span className="font-medium text-foreground/80">{entry.userName}</span>
        </p>
      )}
    </li>
  );
}

const TITULO_DE_ANTES = 'Los cambios de antes de las estrategias';

/** El registro del producto viejo: plegado (decisión 6), con su título y cuántos son. */
function LosDeAntes({
  data: todo,
  estado,
  onReintentar,
  rango = CUALQUIER_FECHA,
}: {
  data: ProductHistoryResponse | null;
  estado: 'CARGANDO' | 'LISTO' | 'ERROR';
  onReintentar: () => void;
  /** Lote B2 (punto 7): «Cambiado», del calendario. */
  rango?: RangoDeFechas;
}) {
  const data = todo
    ? { ...todo, history: todo.history.filter((e) => enElRango(e.createdAt, rango)) }
    : null;
  const filtrado = !!todo && todo.history.length > 0 && data!.history.length === 0;
  if (estado === 'CARGANDO')
    return (
      <div className="space-y-2">
        <h4 className="text-sm font-semibold">{TITULO_DE_ANTES}</h4>
        <AvisoCargando>Cargando los cambios de antes</AvisoCargando>
        <EsqueletoFilas filas={2} />
      </div>
    );
  if (estado === 'ERROR' || !data)
    return (
      <div className="space-y-2">
        <h4 className="text-sm font-semibold">{TITULO_DE_ANTES}</h4>
        <EstadoError onReintentar={onReintentar}>
          No se pudieron traer los cambios de antes.
        </EstadoError>
      </div>
    );
  if (data.history.length === 0)
    return (
      <div className="space-y-2">
        <h4 className="text-sm font-semibold">{TITULO_DE_ANTES}</h4>
        <EstadoVacio titulo={filtrado ? 'Ninguno en esas fechas' : 'Sin cambios de antes'}>
          {filtrado
            ? `Tiene ${todo!.history.length} de antes, en otras fechas.`
            : 'Este producto no tiene cambios anteriores a las estrategias nuevas.'}
        </EstadoVacio>
      </div>
    );

  return (
    <details data-testid="los-de-antes" className="group rounded-lg border">
      <summary className="cursor-pointer px-3 py-2 text-sm font-semibold">
        {TITULO_DE_ANTES} ({data.history.length})
      </summary>
      <div className="space-y-3 border-t p-3">
        <Cifras>
          {/* La columna vieja del producto (`DynamicProduct.strategy`, MARGIN_TARGET por
              omisión), no la estrategia que lo mueve hoy: ésa la dice la explicación de
              arriba (S8). «Estrategia actual» contradecía su «Sin estrategia» (B5). */}
          <Cifra
            chica
            valor={STRATEGY_LABELS[data.product.strategy] || data.product.strategy}
            rotulo="Método de precio del producto"
          />
          <Cifra
            chica
            valor={formatoPrecio(data.product.currentPrice)}
            rotulo="Precio de hoy (Bs)"
          />
          <Cifra chica valor={data.history.length} rotulo="Cambios registrados" />
        </Cifras>
        <ul className="divide-y rounded-md border">
          {data.history.map((entry, i) => (
            <EntradaDeAntes key={entry.id} entry={entry} esLaUltima={i === 0} />
          ))}
        </ul>
      </div>
    </details>
  );
}

export function ProductHistoryDialog({
  open,
  onOpenChange,
  productId,
  productName,
  onVolvio,
}: ProductHistoryDialogProps) {
  const [data, setData] = useState<ProductHistoryResponse | null>(null);
  // Lote C2b (D15): «Volver a este precio», sólo para administradores.
  const { isAdmin } = useAuth();
  const { toast } = useToast();
  // Ronda del 5-oct: el cambio y a dónde vuelve (al que dejó, o al de antes: deshacer).
  const [aVolver, setAVolver] = useState<{
    cambio: CambioDelHistorial;
    aDonde: 'DESPUES' | 'ANTES';
  } | null>(null);
  const [version, setVersion] = useState(0);
  const [estado, setEstado] = useState<'CARGANDO' | 'LISTO' | 'ERROR'>('CARGANDO');
  const [intento, setIntento] = useState(0);
  // Lote B2 (punto 7): el calendario filtra las dos listas por cuándo cambió.
  const [cambiado, setCambiado] = useState<RangoDeFechas>(CUALQUIER_FECHA);
  // Spec 275 (R1-e): con la fila de hoy a la vista, su «Por qué» lleva la
  // explicación y el bloque plegado de arriba no va. Mientras carga, false: el
  // bloque se ve hasta saberlo.
  const [hayFilaDeHoy, setHayFilaDeHoy] = useState(false);

  // Auditoría H20: se limpia al abrir o cerrar y al cambiar de producto (con la
  // ventana abierta, el de otro producto se heredaba).
  useEffect(() => {
    setCambiado(CUALQUIER_FECHA);
  }, [open, productId]);

  useEffect(() => {
    if (!open || !productId) {
      setData(null);
      // Queda listo para la próxima apertura: si quedara «LISTO», al reabrir el
      // primer cuadro dibujaría el error con data en null.
      setEstado('CARGANDO');
      return;
    }
    let cancelled = false;
    setEstado('CARGANDO');
    smartPricingApi
      .getProductHistory(productId)
      .then((res) => {
        if (cancelled) return;
        setData(res);
        setEstado('LISTO');
      })
      .catch((err) => {
        console.error('Error loading product history:', err);
        // Un error no se lee como «sin cambios de antes»: se dice y se reintenta.
        if (!cancelled) setEstado('ERROR');
      });
    return () => {
      cancelled = true;
    };
  }, [open, productId, intento]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] w-[95vw] max-w-[1400px] overflow-y-auto">
        <EncabezadoDeVentana icono={History}>
          <DialogTitle>Historial de precios</DialogTitle>
          <DialogDescription>
            Cada cambio de precio de <strong>{productName}</strong>, venga de donde venga. Las
            fechas, en hora de Bolivia.
          </DialogDescription>
        </EncabezadoDeVentana>

        {/* Plan 4B, S8: UNA explicación de hoy arriba. Lote C2b (maqueta): plegada.
            Spec 275 (R1-e): sólo si no hay fila de hoy (si hay, la explicación
            en vivo vive en el «Por qué» de esa fila). */}
        {productId && !hayFilaDeHoy && (
          <details data-testid="explicacion-de-hoy" className="rounded-lg border">
            <summary className="cursor-pointer px-3 py-2 text-sm font-semibold">
              La explicación de hoy
            </summary>
            <div className="border-t p-3">
              {/* key por versión: después de volver, la explicación se pide de
                  nuevo con el precio nuevo (ronda 2). */}
              <ReglasHoyDelProducto key={version} productId={productId} />
            </div>
          </details>
        )}
        {productId && (
          <HistorialDePrecios
            productId={productId}
            rango={cambiado}
            onRango={setCambiado}
            onVolver={isAdmin ? (cambio, _h, aDonde) => setAVolver({ cambio, aDonde }) : undefined}
            version={version}
            explicacionDeHoy={<ReglasHoyDelProducto key={version} productId={productId} />}
            onHayFilaDeHoy={setHayFilaDeHoy}
          />
        )}
        {productId && (
          <VolverAEstePrecioDialog
            productId={productId}
            productName={productName}
            cambio={aVolver?.cambio ?? null}
            alPrecioDeAntes={aVolver?.aDonde === 'ANTES'}
            onCerrar={() => setAVolver(null)}
            onListo={(r, usadoElSugerido) => {
              setAVolver(null);
              setVersion((n) => n + 1);
              // Y «lo de antes» se re-pide: su «Precio de hoy (Bs)» quedaría con
              // el precio de antes de volver (ronda 2).
              setIntento((n) => n + 1);
              toast({ title: 'Listo', description: quedoEnTexto(r, usadoElSugerido) });
              onVolvio?.(r.precio);
            }}
          />
        )}
        {productId && (
          <LosDeAntes
            data={data}
            estado={estado}
            onReintentar={() => setIntento((n) => n + 1)}
            rango={cambiado}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
